import {
  useEffect,
  cloneElement,
  useState,
  useCallback,
  CSSProperties,
} from 'react';
import * as Sentry from '@sentry/nextjs';
import { NextRouter, useRouter } from 'next/router';

import {
  useSignAmazonPayloadMutation,
  useStartAmazonProcessOrderPaymentMutation,
} from '__generated__/graphql';
import { useSiteConfig } from 'hooks/useSiteConfig';
import {
  PaymentMethod,
  PAYMENT_METHOD_SESSION_STORAGE_ENTRY,
} from 'utils/constants';
import { useFeature } from 'hooks/useFeature';
import { extractError } from 'utils/extractError';
import { ActiveCart, ActiveCartShipmentAddress } from 'types/cart';
import { addPaymentToSessionStorage } from 'utils/addPaymentToSessionStorage';

export type AmazonPayV2ButtonProps = {
  cart?: ActiveCart;
  checkoutMode?: 'Default' | 'ProcessOrder';
  style?: CSSProperties;
};

type AmazonPayV2ChangeButtonProps = {
  id: string;
  children: any;
  type: AmazonPay.ChangeAction;
};

const AMAZON_PAY_PLACEHOLDER = 'AmazonPayButton';

export const isAmazonPaySession = ({
  router,
}: {
  router: NextRouter;
}): {
  isAmazonPay: boolean;
  amazonCheckoutSessionId?: string;
} => {
  const amazonCheckoutSessionId = router.query.amazonCheckoutSessionId;

  const isAmazonPay = typeof amazonCheckoutSessionId === 'string';

  return {
    isAmazonPay,
    amazonCheckoutSessionId: isAmazonPay ? amazonCheckoutSessionId : undefined,
  };
};

const buildPayload = (params: {
  orderNo?: string;
  amazonPayStoreId: string;
  origin: string;
  country: string;
  language: string;
  checkoutMode: string;
  countryCode: string;
  currencyCode: string;
  amount?: number;
  basketId?: string;
  address?: ActiveCartShipmentAddress;
}) => {
  const baseUrl = `${params.origin}/${params.country}/${params.language}`;

  const scopes = ['name', 'email', 'phoneNumber', 'billingAddress'];

  const payload: {
    webCheckoutDetails: Record<string, any>;
    storeId: string;
    scopes: string[];
    addressDetails?: Record<string, string>;
    paymentDetails?: Record<string, any>;
    merchantMetadata?: Record<string, string>;
    deliverySpecifications?: Record<string, any>;
  } = {
    webCheckoutDetails: {},
    storeId: params.amazonPayStoreId,
    scopes,
  };

  const address = params.address;
  const amount = params.amount;
  const basketId = params.basketId;

  // NOTE: These values will feed into `addressDetails` and `paymentDetails`.
  // We need both for a successful order useing `ProcessOrder` option.
  if (
    params.checkoutMode === 'ProcessOrder' &&
    address &&
    amount &&
    basketId &&
    params?.orderNo
  ) {
    payload.addressDetails = {
      name: encodeURI(address.lastName + ' ' + address.firstName),
      addressLine1: encodeURI(address.city),
      addressLine2: encodeURI(address.address1),
      ...(address.companyName && { addressLine3: address.companyName }),
      stateOrRegion: encodeURI(address.stateCode),
      postalCode: address.postalCode,
      countryCode: params.countryCode.toUpperCase(),
      phoneNumber: address.phone ?? '',
    };

    payload.merchantMetadata = {
      merchantReferenceId: params.orderNo,
    };

    payload.webCheckoutDetails.checkoutMode = 'ProcessOrder';

    // NOTE: We must **NOT** set `checkoutReviewReturnUrl` when `checkoutMode`
    // is `ProcessOrder`. Amazon will throw an error with a totally misleading
    // error message.
    payload.webCheckoutDetails.checkoutResultReturnUrl =
      baseUrl + '/checkout/validation/amazon/' + params.orderNo;

    payload.paymentDetails = {
      paymentIntent: 'Authorize',
      canHandlePendingAuthorization: true,
      chargeAmount: {
        amount,
        currencyCode: params.currencyCode,
      },
    };
  } else {
    // NOTE: If there is no `providedAddress` -> we pull the address from the
    // wallet and we need a review step.
    payload.webCheckoutDetails.checkoutReviewReturnUrl = baseUrl + '/checkout';
    //Only Japan addresses
    payload.deliverySpecifications = {
      addressRestrictions: {
        type: 'Allowed',
        restrictions: {
          JP: {},
        },
      },
    };
  }

  return payload;
};

export const AmazonV2EditButton = (props: AmazonPayV2ChangeButtonProps) => {
  const router = useRouter();

  const { amazonPayScriptUrl } = useSiteConfig();

  const isAmazonPayEnabled = useFeature(PaymentMethod.AMAZON_PAY);

  const [amazonScriptLoaded, setAmazonScriptLoaded] = useState(false);

  const amazonCheckoutSessionId = router.query.amazonCheckoutSessionId;

  useEffect(() => {
    if ((amazonScriptLoaded || !!window.amazon) && isAmazonPayEnabled) {
      window.amazon!.Pay.bindChangeAction('#' + props.id, {
        amazonCheckoutSessionId: amazonCheckoutSessionId as string,
        changeAction: props.type,
      });
    }
  }, [
    amazonScriptLoaded,
    isAmazonPayEnabled,
    amazonCheckoutSessionId,
    props.id,
    props.type,
  ]);

  useEffect(() => {
    const script = document.createElement('script');

    script.src = amazonPayScriptUrl as string;

    script.onload = () => {
      setAmazonScriptLoaded(() => true);
    };

    if (!window.amazon) document.body.appendChild(script);
  }, [amazonPayScriptUrl]);

  if (!isAmazonPayEnabled) {
    return null;
  }

  return cloneElement(props.children, { id: props.id });
};

const AmazonPayV2PayButtonInternal = (props: AmazonPayV2ButtonProps) => {
  const { checkoutMode = 'Default' } = props;
  const [, signAmazonPayload] = useSignAmazonPayloadMutation();
  const [, startAmazonProcessOrderPayment] =
    useStartAmazonProcessOrderPaymentMutation();

  const router = useRouter();

  const isAmazonPayEnabled = useFeature(PaymentMethod.AMAZON_PAY);

  const {
    amazonPayScriptUrl,
    locale,
    countryCode,
    currency: { code: currencyCode },
    env: { amazonPayMerchantId, amazonPayPublicKeyId, amazonPayStoreId },
  } = useSiteConfig();

  const [amazonScriptLoaded, setAmazonScriptLoaded] = useState(false);

  const initializeAmazonPay = useCallback(async () => {
    if (
      !amazonPayPublicKeyId ||
      !amazonPayMerchantId ||
      !currencyCode ||
      !locale ||
      !window.amazon ||
      !amazonPayStoreId
    ) {
      Sentry.captureException(
        new Error('Configuration is missing for AmazonPay.')
      );

      return;
    }

    const amazonButton = window.amazon.Pay.renderButton(
      '#' + AMAZON_PAY_PLACEHOLDER,
      {
        merchantId: amazonPayMerchantId,
        publicKeyId: amazonPayPublicKeyId,
        ledgerCurrency: currencyCode,
        checkoutLanguage: locale.replace('-', '_'),
        productType: 'PayAndShip',
        placement: checkoutMode === 'ProcessOrder' ? 'Checkout' : 'Cart',
        buttonColor: 'LightGray',
      }
    );

    amazonButton.onClick(async () => {
      let orderNo: string | undefined;

      if (checkoutMode === 'ProcessOrder' && props.cart?.id) {
        const operationResult = await startAmazonProcessOrderPayment({
          input: { basketId: props.cart.id },
        });

        orderNo =
          operationResult.data?.startAmazonProcessOrderPayment?.order?.orderNo;

        if (!orderNo) return;

        addPaymentToSessionStorage({
          orderNo,
          paymentMethod: PaymentMethod.AMAZON_PAY,
          orderNoEntryName: PAYMENT_METHOD_SESSION_STORAGE_ENTRY.Amazon,
        });
      } else if (checkoutMode !== 'ProcessOrder') {
        // NOTE: A safety cleanup check to remove all previous sessionStorage
        // entries.
      }

      const payload = JSON.stringify(
        buildPayload({
          orderNo,
          amazonPayStoreId,
          language: router.query.language as string,
          country: router.query.country as string,
          origin: window.location.origin,
          checkoutMode,
          currencyCode,
          amount: props.cart?.amountRemaining,
          basketId: props.cart?.id,
          address: props.cart?.shipment?.address,
          countryCode,
        })
      );

      const operationResult = await signAmazonPayload({
        input: { payload },
      });

      const signedPayload =
        operationResult.data?.signAmazonPayload.signedPayload;

      if (!signedPayload) {
        Sentry.captureException(
          new Error(
            extractError(operationResult) ??
              'Was not able to sign amazon payload.'
          )
        );

        return;
      }

      amazonButton.initCheckout({
        createCheckoutSessionConfig: {
          payloadJSON: payload,
          signature: signedPayload,
        },
      });
    });
  }, [
    startAmazonProcessOrderPayment,
    locale,
    signAmazonPayload,
    amazonPayPublicKeyId,
    amazonPayMerchantId,
    checkoutMode,
    props.cart?.id,
    props.cart?.amountRemaining,
    props.cart?.shipment?.address,
    amazonPayStoreId,
    router.query.language,
    router.query.country,
    countryCode,
    currencyCode,
  ]);

  useEffect(() => {
    if ((amazonScriptLoaded || !!window.amazon) && isAmazonPayEnabled) {
      initializeAmazonPay();
    }
  }, [amazonScriptLoaded, initializeAmazonPay, isAmazonPayEnabled]);

  useEffect(() => {
    const script = document.createElement('script');

    script.src = amazonPayScriptUrl as string;

    script.onload = () => {
      setAmazonScriptLoaded(() => true);
    };

    if (!window.amazon) document.body.appendChild(script);
  }, [amazonPayScriptUrl]);

  // NOTE: `style` is used instead of class as class seems to be overriden by Amazon's JS.
  return (
    <div
      id={AMAZON_PAY_PLACEHOLDER}
      data-test-id="amazon-pay-button-note"
      style={props.style}
    />
  );
};

export const AmazonPayV2PayButton = (props: AmazonPayV2ButtonProps) => {
  const isAmazonPayEnabled = useFeature(PaymentMethod.AMAZON_PAY);

  if (!isAmazonPayEnabled) {
    return null;
  }

  return <AmazonPayV2PayButtonInternal {...props} />;
};
